// Enums
export enum Pod {
  CAMPAIGN = "Campaign",
  JOURNEYS = "Journeys",
  HELPDESK = "Helpdesk",
  AI_AGENTS = "AI Agents",
  MANIFEST_AI = "Manifest AI",
}

export enum ChannelSupport {
  WhatsApp = "whatsapp",
  SMS = "sms",
  Email = "email",
  RCS = "rcs",
  WebWidgets = "webpush",
  Instagram = "instagram",
}

export enum BusinessGoal {
  IncreaseEngagement = "increase_engagement",
  ImproveROI = "improve_roi",
  AcquireLeads = "acquire_leads",
  RecoverDropsOff = "recover_drops_off",
  ReduceSupportLead = "reduce_support_lead",
  AutomateWorkflows = "automate_workflows",
  ImproveCX = "improve_cx",
}

export enum Status {
  Live = "live",
  Beta = "beta",
  ComingSoon = "coming soon",
  Deprecated = "deprecated",
  FeatureUpgrade = "feature upgrade",
}

export enum TimelineFilter {
  New = "new",
  ComingSoon = "coming_soon",
  Beta = "beta",
  Live = "live",
  ComingSoonStatus = "coming_soon_status",
  Deprecated = "deprecated",
}

export enum ActivationStatus {
  Enabled = "Enabled",
  NotEnabled = "Not enabled",
}

export enum Availability {
  Available = "Available",
  Locked = "Locked",
}

export enum FeatureImportance {
  XXL = "xxl",
  XL = "xl",
  L = "l",
  M = "m",
  S = "s",
}

export enum Product {
  BIK = "bik",
  Manifest = "manifest",
}

export enum TargetTeam {
  Marketing = "marketing",
  Support = "support",
}

// Models
export interface FAQ {
  q: string;
  a: string;
}

export interface UseCase {
  scenario: string;
  howItHelps: string;
}

export interface ServiceScreenFeature {
  feature_id: string;
  name: string;
  short_description: string;
  long_description: string;
  pod: Pod;
  goals?: string[];
  channels_supported: ChannelSupport[];
  status: Status;
  release_date: string; // Format: "dd-mm-yyyy"
  availability: Availability;
  enable: boolean;
  visible: boolean; 
  enable_flow?: string; // URL or "manual"
  dependencies?: string;
  similar_features?: string[];
  thumbnail_url: string;
  banner_url: string;
  works_best_with_features: string[];
  help_doc_url?: string;
  video_url?: string;
  faq?: FAQ[];
  large_description: string;
  key_benefits?: string;
  use_cases?: UseCase[];
  steps_to_enable?: string;
  product: Product;
  feature_importance: FeatureImportance;
  is_ai: boolean;
  target_team: TargetTeam;
  is_popular: boolean;
  release_jet_feature_name?: string;
  createdAt?: string;
  updatedAt?: string;
  timeline?: string; // Timeline value from API: "live", "new", "coming soon", "beta", "deprecated", "coming_soon_status"
}

export interface FeatureFilters {
  timeline?: TimelineFilter[];
  channelSupport?: ChannelSupport[];
  businessGoal?: BusinessGoal[];
  activationStatus?: (ActivationStatus.Enabled | ActivationStatus.NotEnabled)[];
  pod?: Pod[];
  searchText?: string; // Fuzzy search on name and long_description
}
export interface GetFeaturesListResponse {
  status: number;
  response: {
    features: ServiceScreenFeature[];
    totalCount: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface GetFeatureByIdRequest {
  token: string;
  featureId: string;
}

export interface GetFeatureByIdResponse {
  status: number;
  response: {
    feature: ServiceScreenFeature;
  };
}

export interface CreateFeatureRequest {
  token: string;
  feature_id: string;
  name: string;
  short_description: string;
  long_description: string;
  pod: Pod;
  channels_supported: ChannelSupport[];
  status: Status;
  release_date: string;
  availability: Availability;
  enable: boolean;
  visible: boolean;
  thumbnail_url: string;
  banner_url?: string;
  works_best_with_features: string[];
  large_description: string;
  product: Product;
  feature_importance: FeatureImportance;
  is_ai: boolean;
  target_team: TargetTeam;
  is_popular: boolean;
  goals?: string[];
  enable_flow?: string;
  dependencies?: string;
  similar_features?: string[];
  help_doc_url?: string;
  video_url?: string;
  faq?: FAQ[];
  key_benefits?: string;
  use_cases?: UseCase[];
  steps_to_enable?: string;
  release_jet_feature_name?: string;
}

export interface CreateFeatureResponse {
  status: number;
  response: {
    feature: ServiceScreenFeature;
  };
}

export interface UpdateFeatureRequest {
  token: string;
  featureId: string;
  name?: string;
  short_description?: string;
  long_description?: string;
  pod?: Pod;
  goals?: string[];
  channels_supported?: ChannelSupport[];
  status?: Status;
  release_date?: string;
  availability?: Availability;
  enable?: boolean;
  visible?: boolean;
  enable_flow?: string;
  dependencies?: string;
  similar_features?: string[];
  thumbnail_url?: string;
  banner_url?: string;
  works_best_with_features?: string[];
  help_doc_url?: string;
  video_url?: string;
  faq?: FAQ[];
  large_description?: string;
  key_benefits?: string;
  use_cases?: UseCase[];
  steps_to_enable?: string;
  product?: Product;
  feature_importance?: FeatureImportance;
  is_ai?: boolean;
  target_team?: TargetTeam;
  is_popular?: boolean;
  release_jet_feature_name?: string;
}

export interface UpdateFeatureResponse {
  status: number;
  response: {
    feature: ServiceScreenFeature;
  };
}

export interface DeleteFeatureRequest {
  token: string;
  featureId: string;
}

export interface DeleteFeatureResponse {
  status: number;
  response: {
    success: boolean;
  };
}

export interface BulkUploadFeaturesRequest {
  token: string;
  fileUrl: string;
}

export interface BulkUploadFeaturesResponse {
  status: number;
  response: {
    successCount: number;
    errorCount: number;
    errorFileUrl?: string;
    warnings?: string[];
  };
}

export interface DownloadFeaturesListRequest {
  token: string;
}

export interface DownloadFeaturesListResponse {
  status: number;
  response: {
    downloadUrl: string;
  };
}
